import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import apiService from "../../services/ApiService";
import { ClassData } from "../../model/ClassData";

interface ClassesState {
  classes: ClassData[];
  currentClass: ClassData | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: ClassesState = {
  classes: [],
  currentClass: null,
  isLoading: false,
  error: null,
};

export const fetchClasses = createAsyncThunk<
  ClassData[],
  string | undefined,
  { rejectValue: string }
>("classes/fetchClasses", async (schoolId, { rejectWithValue }) => {
  const res = await apiService.getClasses(schoolId);
  if (res.success && res.data) return res.data as ClassData[];
  return rejectWithValue(res.message || "Failed to fetch classes");
});

export const fetchClass = createAsyncThunk<
  ClassData,
  string,
  { rejectValue: string }
>("classes/fetchClass", async (id, { rejectWithValue }) => {
  const res = await apiService.getClass(id);
  if (res.success && res.data) return res.data as ClassData;
  return rejectWithValue(res.message || "Failed to fetch class");
});

export const createClass = createAsyncThunk<
  ClassData,
  { name: string; schoolId?: string },
  { rejectValue: string }
>("classes/createClass", async (data, { rejectWithValue }) => {
  const res = await apiService.createClass(data);
  if (res.success && res.data) return res.data as ClassData;
  return rejectWithValue(res.message || "Failed to create class");
});

export const updateClass = createAsyncThunk<
  ClassData,
  { id: string; name: string },
  { rejectValue: string }
>("classes/updateClass", async ({ id, name }, { rejectWithValue }) => {
  const res = await apiService.updateClass(id, { name });
  if (res.success && res.data) return res.data as ClassData;
  return rejectWithValue(res.message || "Failed to update class");
});

export const deleteClass = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("classes/deleteClass", async (id, { rejectWithValue }) => {
  const res = await apiService.deleteClass(id);
  if (res.success || res.status === 204) return id;
  return rejectWithValue(res.message || "Failed to delete class");
});

const classesSlice = createSlice({
  name: "classes",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentClass: (state) => {
      state.currentClass = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClasses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchClasses.fulfilled,
        (state, action: PayloadAction<ClassData[]>) => {
          state.isLoading = false;
          state.classes = action.payload;
        },
      )
      .addCase(fetchClasses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch classes";
      })
      .addCase(fetchClass.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchClass.fulfilled,
        (state, action: PayloadAction<ClassData>) => {
          state.isLoading = false;
          state.currentClass = action.payload;
        },
      )
      .addCase(fetchClass.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch class";
      })
      .addCase(createClass.fulfilled, (state, action) => {
        state.classes.unshift(action.payload);
      })
      .addCase(createClass.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to create class";
      })
      .addCase(updateClass.fulfilled, (state, action) => {
        const idx = state.classes.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.classes[idx] = action.payload;
        if (state.currentClass?.id === action.payload.id) {
          state.currentClass = {
            ...state.currentClass,
            ...action.payload,
          };
        }
      })
      .addCase(updateClass.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to update class";
      })
      .addCase(deleteClass.fulfilled, (state, action) => {
        state.classes = state.classes.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteClass.rejected, (state, action) => {
        state.error = action.payload ?? "Failed to delete class";
      });
  },
});

export const { clearError, clearCurrentClass } = classesSlice.actions;

export const selectClasses = (state: { classes: ClassesState }) =>
  state.classes.classes;
export const selectCurrentClass = (state: { classes: ClassesState }) =>
  state.classes.currentClass;
export const selectClassesLoading = (state: { classes: ClassesState }) =>
  state.classes.isLoading;
export const selectClassesError = (state: { classes: ClassesState }) =>
  state.classes.error;

export default classesSlice.reducer;
